import type { Problem } from "@ppx/shared-types";
import { ApiError, request } from "./api";
import type {
  Activity,
  ActivityInput,
  ActivityListParams,
  Company,
  CompanyInput,
  CompanyListParams,
  Contact,
  ContactInput,
  ContactListParams,
  CrmStats,
  Deal,
  DealInput,
  DealListParams,
  DealUpdateInput,
  Page,
  Pipeline,
  Stage,
  StageInput,
} from "./crm-types";

type QueryValue = string | number | boolean | undefined;

function qs(params: Record<string, QueryValue>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "" || value === false) {
      continue;
    }
    search.set(key, String(value));
  }
  const serialized = search.toString();
  return serialized === "" ? "" : `?${serialized}`;
}

function toCommon(params: { q?: string; cursor?: string; limit?: number }): Record<string, QueryValue> {
  return {
    q: params.q,
    cursor: params.cursor,
    limit: params.limit,
  };
}

export function listCompanies(params: CompanyListParams = {}): Promise<Page<Company>> {
  return request<Page<Company>>(
    `/crm/companies${qs({ ...toCommon(params), status: params.status, owner: params.owner, tag: params.tag })}`,
  );
}

export function getCompany(id: string): Promise<Company> {
  return request<Company>(`/crm/companies/${encodeURIComponent(id)}`);
}

export function createCompany(input: CompanyInput): Promise<Company> {
  return request<Company>("/crm/companies", { method: "POST", body: input });
}

export function updateCompany(id: string, input: Partial<CompanyInput>): Promise<Company> {
  return request<Company>(`/crm/companies/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: input,
  });
}

export function listContacts(params: ContactListParams = {}): Promise<Page<Contact>> {
  return request<Page<Contact>>(
    `/crm/contacts${qs({ ...toCommon(params), status: params.status, owner: params.owner, company_id: params.company_id })}`,
  );
}

export function getContact(id: string): Promise<Contact> {
  return request<Contact>(`/crm/contacts/${encodeURIComponent(id)}`);
}

export function createContact(input: ContactInput): Promise<Contact> {
  return request<Contact>("/crm/contacts", { method: "POST", body: input });
}

export function updateContact(id: string, input: Partial<ContactInput>): Promise<Contact> {
  return request<Contact>(`/crm/contacts/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: input,
  });
}

export function listPipelines(includeArchived = false): Promise<{ items: Pipeline[] }> {
  return request<{ items: Pipeline[] }>(
    `/crm/pipelines${qs({ include_archived: includeArchived ? "true" : undefined })}`,
  );
}

export function listStages(pipelineId: string): Promise<{ items: Stage[] }> {
  return request<{ items: Stage[] }>(`/crm/pipelines/${encodeURIComponent(pipelineId)}/stages`);
}

export function createStage(pipelineId: string, input: StageInput): Promise<Stage> {
  return request<Stage>(`/crm/pipelines/${encodeURIComponent(pipelineId)}/stages`, {
    method: "POST",
    body: input,
  });
}

export function reorderStages(pipelineId: string, stageIds: string[]): Promise<{ items: Stage[] }> {
  return request<{ items: Stage[] }>(`/crm/pipelines/${encodeURIComponent(pipelineId)}/reorder`, {
    method: "POST",
    body: { stage_ids: stageIds },
  });
}

export function listDeals(params: DealListParams = {}): Promise<Page<Deal>> {
  return request<Page<Deal>>(
    `/crm/deals${qs({
      ...toCommon(params),
      status: params.status,
      owner: params.owner,
      pipeline_id: params.pipeline_id,
      stage_id: params.stage_id,
      company_id: params.company_id,
      contact_id: params.contact_id,
    })}`,
  );
}

export function getDeal(id: string): Promise<Deal> {
  return request<Deal>(`/crm/deals/${encodeURIComponent(id)}`);
}

export function createDeal(input: DealInput): Promise<Deal> {
  return request<Deal>("/crm/deals", { method: "POST", body: input });
}

export function updateDeal(id: string, input: DealUpdateInput): Promise<Deal> {
  return request<Deal>(`/crm/deals/${encodeURIComponent(id)}`, { method: "PATCH", body: input });
}

export function moveDealStage(
  id: string,
  input: { stage_id: string; lost_reason?: string },
): Promise<Deal> {
  return request<Deal>(`/crm/deals/${encodeURIComponent(id)}/stage`, {
    method: "POST",
    body: input,
  });
}

export function listActivities(params: ActivityListParams = {}): Promise<Page<Activity>> {
  return request<Page<Activity>>(
    `/crm/activities${qs({
      ...toCommon(params),
      status: params.status,
      owner: params.owner,
      kind: params.kind,
      related_type: params.related_type,
      related_id: params.related_id,
    })}`,
  );
}

export function createActivity(input: ActivityInput): Promise<Activity> {
  return request<Activity>("/crm/activities", { method: "POST", body: input });
}

export function completeActivity(id: string): Promise<Activity> {
  return request<Activity>(`/crm/activities/${encodeURIComponent(id)}/complete`, {
    method: "POST",
  });
}

export function fetchCrmStats(): Promise<CrmStats> {
  return request<CrmStats>("/crm/stats/overview");
}

export function duplicateCandidateId(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.code !== "crm_duplicate") {
    return null;
  }
  const problem = error.problem as (Problem & { dedupe_candidate_id?: unknown }) | null;
  const value = problem?.dedupe_candidate_id;
  return typeof value === "string" && value !== "" ? value : null;
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}
